/* eslint-disable */
import { useState, useEffect, useMemo } from 'react';
import { Modal } from 'antd';
import { useDataContext } from '../context';
import { useCookies } from 'react-cookie';
import { jwtDecode } from 'jwt-decode';
import {
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Avatar,
  Divider,
  CircularProgress,
  Chip,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  Collapse,
  IconButton,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { LoadingOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Iconify from 'src/components/iconify';

// Plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// ✅ ใช้ตัวแปรจาก Environment
const API_URL = import.meta.env.VITE_API_URL;

// ----------------------------------------------------------------------
// 1. Component: ImageFromApi (คงเดิม)
// ----------------------------------------------------------------------
const ImageFromApi = ({ fileUrl, accessToken, isMobile }) => {
  const [fileData, setFileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!fileUrl || !accessToken) return;

    let isMounted = true;
    setLoading(true);
    setError(false);
    setFileData(null);

    const fetchFile = async () => {
      try {
        const res = await fetch(`${API_URL}/proxyFetchUrl`, { // ✅ ใช้ API_URL
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: fileUrl, token: accessToken }),
        });
        const data = await res.json();

        if (isMounted) {
          if (data.status === 'success') {
            setFileData({
              type: data.contentType || 'image/jpeg',
              src: data.dataUri,
            });
          } else {
            setError(true);
          }
        }
      } catch (err) {
        console.error('❌ Error loading file:', err);
        if (isMounted) setError(true);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchFile();

    return () => {
      isMounted = false;
    };
  }, [fileUrl, accessToken]);

  const blobUrl = useMemo(() => {
    if (!fileData || !fileData.type.includes('pdf')) return null;
    try {
      const base64Data = fileData.src.split(',')[1] || fileData.src;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('Error creating blob:', err);
      return null;
    }
  }, [fileData]);

  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, bgcolor: '#f9fafb', borderRadius: 2, border: '1px dashed #e0e0e0' }}>
        <LoadingOutlined spin style={{ fontSize: 28, color: '#1677ff' }} />
        <Typography sx={{ ml: 2, color: 'text.secondary', fontFamily: 'Noto Sans Thai' }}>กำลังโหลดเอกสาร...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', bgcolor: '#ffebee', borderRadius: 2, border: '1px dashed #ef5350' }}>
        <Typography variant="body2" color="error" sx={{ fontFamily: 'Noto Sans Thai' }}>❌ ไม่สามารถดาวน์โหลดไฟล์ได้</Typography>
      </Box>
    );
  }

  if (fileData?.type?.includes('pdf') && blobUrl) {
    if (isMobile) {
        return (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f5f7fa', borderRadius: 2, border: '1px solid #e0e0e0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <FilePdfOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontFamily: 'Noto Sans Thai' }}>เอกสารรูปแบบ PDF</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Noto Sans Thai', mb: 2 }}>เบราว์เซอร์บนมือถือไม่รองรับการแสดงตัวอย่าง</Typography>
                    <Button variant="contained" color="error" onClick={() => window.open(blobUrl, '_blank')} startIcon={<Iconify icon="eva:external-link-fill" />} sx={{ fontFamily: 'Noto Sans Thai', borderRadius: 20 }}>
                        แตะเพื่อเปิดอ่านเอกสารฉบับเต็ม
                    </Button>
                </Box>
            </Box>
        );
    }
    return (
      <Box sx={{ width: '100%', height: '600px', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
        <iframe src={blobUrl} width="100%" height="100%" title="PDF Preview" style={{ border: 'none' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', bgcolor: '#f4f6f8', borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
      <img src={fileData?.src} alt="Preview" style={{ maxWidth: '100%', maxHeight: '600px', display: 'block', margin: '0 auto' }} />
    </Box>
  );
};

// ----------------------------------------------------------------------
// 2. Utility & Viewers
// ----------------------------------------------------------------------
const parseDocPaths = (path) => {
  if (!path) return [];
  try {
    const parsed = JSON.parse(path);
    if (Array.isArray(parsed)) return parsed;
    return [path];
  } catch (e) {
    return [path];
  }
};

const FileViewer = ({ urls, accessToken, isMobile }) => {
  if (!urls || urls.length === 0) return null;

  return (
    <Stack spacing={3}>
      {urls.map((url, index) => (
        <Box key={`${url}-${index}`} sx={{ position: 'relative' }}>
          {urls.length > 1 && (
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: 'text.secondary', fontWeight: 'bold', fontFamily: 'Noto Sans Thai' }}>
              เอกสารแนบที่ {index + 1}
            </Typography>
          )}
          <ImageFromApi fileUrl={url} accessToken={accessToken} isMobile={isMobile} />
        </Box>
      ))}
    </Stack>
  );
};

// ----------------------------------------------------------------------
// 3. Main Component: AllDepartment
// ----------------------------------------------------------------------
function AllDepartment() {
  const { data, setData } = useDataContext(); // ✅ ใช้ data จาก Context
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [open, setOpen] = useState(false);
  const [cookies] = useCookies(['session', 'token']);
  const [loadingId, setLoadingId] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [openedDocs, setOpenedDocs] = useState([]);
  const [isAckChecked, setIsAckChecked] = useState(false);
 
  const apiToken = cookies.token || cookies.session;

  const { userName, isCandidate } = useMemo(() => {
    if (!apiToken || typeof apiToken !== 'string') {
      return { userName: null, isCandidate: false };
    }

    try {
      const decoded = jwtDecode(apiToken);
      if (decoded.candidateId) {
        return { userName: null, isCandidate: true };
      }

      const foundName = 
        decoded.preferred_username || 
        decoded.upn || 
        decoded.unique_name || 
        decoded.email || 
        decoded.UserName || 
        decoded.username || 
        decoded.description || 
        decoded.sub;

      return { userName: foundName, isCandidate: false };

    } catch (error) {
      console.error('Error decoding JWT:', error.message);
      return { userName: null, isCandidate: false };
    }
  }, [apiToken]);

  // ✅ ใช้ data.docOther จาก Context แทนการ fetch ใหม่
  const docsWithUser = useMemo(() => {
    return data?.docOther?.filter((doc) => {
      // ถ้าเคยดูแล้วให้ข้าม
      if (doc.viewers && doc.viewers.includes(userName)) return false;
      
      // เช็ควันที่
      if (!doc.docDate) return false;
      const docDateStr = dayjs(doc.docDate).format('YYYY-MM-DD');
      const startDate = '2025-12-19'; 
      return docDateStr >= startDate; 
    }) || [];
  }, [data?.docOther, userName]);

  useEffect(() => {
    const hasDocuments = docsWithUser.length > 0;
    const hasUser = !!userName;
    const hasSession = !!cookies.session;
    
    if (hasDocuments && hasSession && hasUser && !isCandidate) {
      setOpen(true);
    } else {
      setOpen(false);
      setOpenedDocs([]);
      setExpandedId(null);
    }
  }, [docsWithUser, cookies.session, userName, isCandidate]);

  // ❌ ลบฟังก์ชัน fetchData() ออก เพราะซ้ำซ้อนกับ Global Service
  /*
  const fetchData = () => {
    fetch('https://api-hr.boonsiri.co.th/documents', ...)
      .then(...)
  };
  */

  const handleToggleDoc = (docId) => {
    const newExpandedId = expandedId === docId ? null : docId;
    setExpandedId(newExpandedId);
    if (newExpandedId === docId) setIsAckChecked(false);
    if (newExpandedId === docId && !openedDocs.includes(docId)) {
      setOpenedDocs((prev) => [...prev, docId]);
    }
  };

  const handleAcknowledgeDoc = async (doc) => {
    setLoadingId(doc.docId);
    const updatedViewers = doc.viewers ? `${doc.viewers}, ${userName}` : userName;
    
    try {
      const response = await fetch(`${API_URL}/documents/${doc.docId}`, { // ✅ ใช้ API_URL
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Basic SHJAYm9vbnNpcmkuY28udGg6SHJAYnNyISEh',
        },
        body: JSON.stringify({ ...doc, viewers: updatedViewers }),
      });

      if (response.ok) {
        // ✅ อัปเดต Context ทันทีโดยไม่ต้อง Fetch ใหม่ (Optimistic Update)
        // เพื่อไม่ให้เกิด Duplicate Request
        setData((prevData) => ({
            ...prevData,
            docOther: prevData.docOther.map((d) => 
                d.docId === doc.docId ? { ...d, viewers: updatedViewers } : d
            )
        }));
      }
    } catch (error) {
      console.error('Error updating document:', error);
    } finally {
      setLoadingId(null);
    }
  };

  const formatDate = (date) => dayjs.utc(date).tz('Asia/Bangkok').format('DD MMM YYYY • HH:mm');

  return (
    <Modal
      styles={{
        body: { 
            padding: 0, 
            height: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 80px)', 
            overflowY: 'auto',
            backgroundColor: '#F4F6F8'
        },
        content: isMobile ? { 
            height: '100vh', 
            margin: 0, 
            padding: 0, 
            borderRadius: 0,
            overflow: 'hidden' 
        } : {}
      }}
      centered
      open={open}
      closable={false}
      maskClosable={false}
      footer={null}
      width={isMobile ? '100%' : '100vw'}
      style={isMobile ? { top: 0, margin: 0, maxWidth: '100vw', padding: 0 } : { top: 0, margin: 0, maxWidth: '100vw', padding: 0 }}
      zIndex={99999}
      title={
        <Container maxWidth={false} sx={{ width: { xs: '100%', md: '90%' }, mx: 'auto', px: { xs: 1, md: 0 } }}>
          <Stack direction="row" alignItems="center" gap={1.5} py={2} borderBottom="1px solid #f0f0f0">
            <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'error.lighter', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon="solar:bell-bing-bold-duotone" width={24} color="#FF4842" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontFamily: 'Noto Sans Thai', fontWeight: 700, lineHeight: 1.2 }}>
                ประกาศที่ต้องรับทราบ ({docsWithUser.length})
              </Typography>
            </Box>
          </Stack>
        </Container>
      }
    >
      <Box sx={{ minHeight: '100%', bgcolor: '#F4F6F8', py: 3 }}>
        <Container maxWidth={false} sx={{ width: { xs: '100%', md: '90%' }, mx: 'auto', px: { xs: 1, md: 2 } }}>
          <Stack spacing={2}>
            {docsWithUser.map((doc, index) => {
              const isRead = openedDocs.includes(doc.docId);
              const isExpanded = expandedId === doc.docId;
              const fileUrls = parseDocPaths(doc.docPath);

              return (
                <Fade in={true} timeout={300 + index * 100} key={doc.docId}>
                  <Paper
                    elevation={isExpanded ? 3 : 0}
                    sx={{
                      borderRadius: 3,
                      bgcolor: '#fff',
                      border: '1px solid',
                      borderColor: isExpanded ? 'primary.main' : 'divider',
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Box
                      onClick={() => handleToggleDoc(doc.docId)}
                      sx={{
                        p: 2.5,
                        cursor: 'pointer',
                        bgcolor: isExpanded ? 'primary.lighter' : '#fff',
                        '&:hover': { bgcolor: isExpanded ? 'primary.lighter' : '#fafafa' },
                        display: 'flex', alignItems: 'flex-start', gap: 2
                      }}
                    >
                      <Avatar sx={{ bgcolor: isRead ? 'success.main' : 'primary.main', color: '#fff', width: 48, height: 48 }}>
                        <Iconify icon={isRead ? 'solar:check-read-bold-duotone' : 'solar:document-text-bold-duotone'} width={24} />
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                          <Typography variant="subtitle1" sx={{ fontFamily: 'Noto Sans Thai', fontWeight: 700, color: '#212B36' }}>
                            {doc.docTitle}
                          </Typography>
                          <IconButton size="small" sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: '0.3s' }}>
                            <Iconify icon="eva:arrow-ios-downward-fill" />
                          </IconButton>
                        </Stack>
                        <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} mt={0.5}>
                          <Typography variant="caption" sx={{ fontFamily: 'Noto Sans Thai', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Iconify icon="solar:calendar-date-bold" width={14} />
                            {formatDate(doc.docDate || doc.uploadDate)}
                          </Typography>
                          {doc.createBy && (
                            <Typography variant="caption" sx={{ fontFamily: 'Noto Sans Thai', color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Iconify icon="solar:user-circle-bold" width={14} />
                              {doc.createBy}
                            </Typography>
                          )}
                          {fileUrls.length > 0 && (
                            <Chip label={`${fileUrls.length} ไฟล์แนบ`} size="small" sx={{ height: 20, fontSize: 10, fontFamily: 'Noto Sans Thai' }} />
                          )}
                          {!isMobile && (
                            <Chip label={isRead ? 'เปิดอ่านแล้ว' : 'ยังไม่อ่าน'} color={isRead ? 'success' : 'warning'} size="small" variant="soft" sx={{ height: 20, fontSize: 10, fontFamily: 'Noto Sans Thai' }} />
                          )}
                        </Stack>
                      </Box>
                    </Box>

                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 3, borderTop: '1px dashed #e0e0e0', bgcolor: '#fff' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'Noto Sans Thai', color: 'text.secondary', mb: 3 }}>
                          {doc.description || 'รายละเอียดเอกสารแนบ:'}
                        </Typography>

                        <Box sx={{ mb: 4 }}>
                          <FileViewer urls={fileUrls} accessToken={apiToken} isMobile={isMobile} />
                        </Box>

                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox 
                                        checked={isAckChecked}
                                        onChange={(e) => setIsAckChecked(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ fontFamily: 'Noto Sans Thai', fontWeight: 600 }}>
                                        ข้าพเจ้าได้เปิดอ่านและทำความเข้าใจเนื้อหาในเอกสารแนบครบถ้วนแล้ว
                                    </Typography>
                                }
                            />
                        </Box>

                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="flex-end" spacing={2}>
                          {!isMobile && (
                            <Button
                              variant="outlined" color="inherit" size="large"
                              onClick={(e) => { e.stopPropagation(); if (fileUrls[0]) window.open(fileUrls[0], '_blank'); }}
                              startIcon={<Iconify icon="eva:external-link-fill" />}
                              sx={{ fontFamily: 'Noto Sans Thai', height: 48 }}
                            >
                              {fileUrls.length > 1 ? 'เปิดไฟล์ทั้งหมด (Link)' : 'เปิดลิงก์ต้นทาง'}
                            </Button>
                          )}
                          <Button
                            variant="contained" color="primary" size="large" fullWidth={isMobile}
                            onClick={() => handleAcknowledgeDoc(doc)}
                            disabled={!isAckChecked || loadingId === doc.docId}
                            startIcon={loadingId === doc.docId ? <CircularProgress size={20} color="inherit" /> : <Iconify icon="eva:checkmark-circle-2-fill" />}
                            sx={{ fontFamily: 'Noto Sans Thai', height: 48, px: 4, boxShadow: '0 8px 16px 0 rgba(32, 101, 209, 0.24)' }}
                          >
                            {loadingId === doc.docId ? 'กำลังบันทึก...' : isMobile ? 'รับทราบ' : 'รับทราบและปิดรายการ'}
                          </Button>
                        </Stack>
                      </Box>
                    </Collapse>
                  </Paper>
                </Fade>
              );
            })}
          </Stack>

          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 4, pb: 4, color: 'text.disabled', fontFamily: 'Noto Sans Thai' }}>
            * คลิกที่รายการเพื่อดูรายละเอียดเอกสาร ก่อนกดรับทราบ
          </Typography>
        </Container>
      </Box>
    </Modal>
  );
}

export default AllDepartment;